import React, { useState, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Navbar from "../navbar/Navbar";
import HomePagImg from "../../../Assets/partner_homapag.jpeg";
import Client1 from "../../../Assets/partner-image2.jpg";
import Client2 from "../../../Assets/partner-image1.jpg";
import Client3 from "../../../Assets/partner-image3.webp";
import Client4 from "../../../Assets/partner-image4.jpg";
import creche from "../../../Assets/dog_creche.webp";
import grooming from "../../../Assets/dog_grooming.webp";
import clinic from "../../../Assets/dog_clinic.jpg";
import vet from "../../../Assets/dogvet.webp";
import Pharmacy from "../../../Assets/pet_pharmacy.jpeg";
import Footer from "../../footer/Footer";
import "./PartnerHomePage.css";
import { Helmet } from "react-helmet-async";

interface Review {
  id: number;
  name: string;
  role: string;
  message: string;
  image: string;
}

interface Feature {
  image: string;
  title: string;
  description: string;
}

const reviews: Review[] = [
  {
    id: 1,
    name: "Simran Kaur",
    role: "Partner - Groomer",
    message:
      "PupHub Partner App has streamlined how we connect with pet parents. Managing bookings and interacting with customers has never been easier!",
    image: Client1,
  },
  {
    id: 2,
    name: "Rahul Mehra",
    role: "Partner - Veterinary",
    message:
      "An excellent platform to reach local pet owners! PupHub helps us showcase our services, offer promotions, and schedule appointments effortlessly.",
    image: Client2,
  },
  {
    id: 3,
    name: "Aria Patel",
    role: "Partner - Creche Owner",
    message:
      "PupHub made it easier than ever to manage bookings and reach genuine pet parents. It's been a game-changer for our creche.",
    image: Client3,
  },
  {
    id: 4,
    name: "Rohan Deshmukh",
    role: "Partner - Creche Owner",
    message:
      "PupHub made it easier than ever to manage bookings and reach genuine pet parents. It's been a game-changer for our creche.",
    image: Client4,
  },
];

const features: Feature[] = [
  {
    image: creche,
    title: "Creche Booking",
    description:
      "Safe, loving space for pets while you're away—reliable daily care and supervision.",
  },
  {
    image: grooming,
    title: "Grooming Services",
    description:
      "Professional pet grooming for hygiene and style—baths, trims, and pampering.",
  },
  {
    image: clinic,
    title: "Clinic Appointment",
    description:
      "Complete medical care for pets—vaccinations, diagnostics, surgeries, and emergency services.",
  },
  {
    image: vet,
    title: "Veterinary Consultation",
    description:
      "Certified veterinarians offering consultations, treatments, and health checks for pets.",
  },
  {
    image: Pharmacy,
    title: "Pharmacy",
    description:
      "Pet medicines, supplements, and essentials—quick access to trusted veterinary products.",
  },
];

const PartnerHomePage: React.FC = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState<number>(0);
  const [featureInView, setFeatureInView] = useState<boolean[]>(Array(4).fill(false));
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);
  const [featuresToDisplay, setFeaturesToDisplay] = useState<Feature[]>([]);
  const featuresContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const reviewInterval = setInterval(() => {
      setCurrentReviewIndex((prevIndex) => (prevIndex + 1) % reviews.length);
    }, 5000);

    const featureObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target instanceof HTMLElement) {
            const index = parseInt(entry.target.dataset.index || '0', 10);
            setFeatureInView((prev) => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll(".feature-card").forEach((card, index) => {
      if (card instanceof HTMLElement) {
        card.dataset.index = index.toString();
        featureObserver.observe(card);
      }
    });

    if (videoRef.current) {
      videoRef.current.onloadeddata = () => setIsVideoLoaded(true);
    }

    return () => {
      clearInterval(reviewInterval);
      featureObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    setFeaturesToDisplay([...features, ...features, ...features, ...features, ...features]);
  }, []);

  useEffect(() => {
    const container = featuresContainerRef.current;
    if (container) {
      container.scrollLeft = container.scrollWidth / 3;
    }
  }, [featuresToDisplay]);

  const handleScroll = () => {
    const container = featuresContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth } = container;
    const scrollThreshold = scrollWidth / 3;

    if (scrollLeft < scrollThreshold / 2) {
      container.scrollLeft = scrollThreshold;
    } else if (scrollLeft > scrollThreshold * 1.5) {
      container.scrollLeft = scrollThreshold;
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (featuresContainerRef.current) {
      const scrollAmount = featuresContainerRef.current.clientWidth * 0.8;
      featuresContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const currentReview = reviews[currentReviewIndex];

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://puphub.co.in/partner" />
        <title>PupHub Partner | Home</title>
        <meta
          name="description"
          content="PupHub Partner dashboard for managing pet services."
        />
      </Helmet>
      
      <Navbar />
      
      <div
        className="w-[100vw] h-[30vh] sm:h-[70vh] relative"
        style={{
          backgroundImage: `url(${HomePagImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
        role="img"
        aria-label="PupHub Partner platform for pet service providers"
      />
      
      {/* Features Section */}
      <section className="features-section bg-[#16213e]">
        <h2 className="section-title">
          What Can You Offer Through PupHub Partner?
        </h2>
        <div className="relative">
          <button
            className="scroll-arrow left"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            <FaChevronLeft />
          </button>
          <div
            className="features-scroll-container"
            ref={featuresContainerRef}
            onScroll={handleScroll}
          >
            {featuresToDisplay.map((feature, index) => (
              <div
                className="feature-scroll-card"
                key={`${index}-${feature.title}`}
              >
                <div className="feature-image">
                  <img 
                    src={feature.image} 
                    alt={feature.title} 
                    loading="lazy"
                  />
                </div>
                <div className="feature-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.PupHubPartner"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="add-service-btn"
                    aria-label={`Add ${feature.title} to my services`}
                  >
                    Add to My Service
                  </a>
                </div>
              </div>
            ))}
          </div>
          <button
            className="scroll-arrow right"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            <FaChevronRight />
          </button>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section bg-[#1a1a2e]">
        <h2 className="section-title">What Our Partners Say</h2>
        <div className="testimonial-card">
          <div className="testimonial-image-container">
            <img
              src={currentReview.image}
              alt={currentReview.name}
              className="testimonial-image"
              loading="lazy"
            />
          </div>
          <div className="testimonial-content">
            <blockquote className="testimonial-text">
              "{currentReview.message}"
            </blockquote>
            <div className="testimonial-author">
              <p className="author-name">{currentReview.name}</p>
              <p className="author-role">{currentReview.role}</p>
            </div>
          </div>
        </div>
        <div className="testimonial-dots">
          {reviews.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentReviewIndex ? "active" : ""}`}
              onClick={() => setCurrentReviewIndex(index)}
              aria-label={`View testimonial from ${reviews[index].name}`}
            />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section bg-gradient-to-r from-[#4f46e5] to-[#7c3aed]">
        <h2 className="cta-title">Ready to Grow Your Pet Business with Us?</h2>
        <p className="cta-text">
          Get PupHub Partner today and grow your pet service business
          effortlessly!
        </p>
        <div className="cta-buttons">
          <a
            href="https://play.google.com/store/apps/details?id=com.PupHubPartner"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download PupHub Partner app"
          >
            <button className="cta-button-primary">Download Now</button>
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PartnerHomePage;